import { ref, computed, type Ref } from 'vue';
import { defineStore } from 'pinia';
import type {
  IAuthRequest,
  IAuthResponse,
  IAuthSecondFactorRequest,
  ICurrentEmployeeResponse,
  IAuthRoles,
} from '../models/auth';
import { legacyAxios } from '../utilities/axios';
import type { AxiosResponse } from 'axios';
import packageInfo from '../../package.json';
import { getFeatureFlagsService } from '@/utilities/featureFlagsService';
import { useFeatureFlagsStore } from '@erebus/feature-flags';
import { getRuntimeConfig } from '@erebus/runtime-config';
import {
  login as httpLogin,
  verifyMFA as httpVerifyMFA,
  logout as httpLogout,
  restoreSession,
  getMyRoles,
} from '@erebus/http';
import type {
  AuthMFAChallenge as HttpMFAChallenge,
  AuthMFAEnrollRequired,
  AuthResult,
  RolesResponse,
} from '@erebus/http';

export const useAuthStore = defineStore('auth', () => {
  const { useCookieAuth } = getRuntimeConfig();

  const sessionActive = ref(false);
  const mfaRecommended = ref(false);
  const pendingMFAChallenge = ref<HttpMFAChallenge | null>(null);
  /**
   * The challenge token from a `must_reset` login, and the only credential the
   * user holds until they set a new password — they have no session yet.
   *
   * Login and MFA verify each hand out their own, and they are not
   * interchangeable: the one carried through the MFA step is refused by
   * /auth/reset-password. Always keep the newest. It is single-use and lasts
   * 15 minutes, so it is held in memory only and never written to storage.
   */
  const pendingResetToken = ref<string | null>(null);
  /**
   * The setup token from an `mfa_enroll_required` login (PRX-2355): the user
   * holds a role that requires MFA and has none yet. Like the reset token it is
   * the only credential they hold, lasts 15 minutes, and stays in memory.
   */
  const pendingMFASetup = ref<AuthMFAEnrollRequired | null>(null);

  const isAuthenticated = computed(() =>
    useCookieAuth ? sessionActive.value : authToken.value != null,
  );

  const isSuperAdmin = computed(() => {
    const roles = useCookieAuth
      ? (sessionRoles.value?.global?.roles ?? [])
      : (authRoles.value?.global?.roles ?? []);
    return roles.includes('masterAdmin') || roles.includes('admin');
  });

  const isScheduleAdmin = computed(() => {
    const SCHEDULE_ADMIN_ROLES = ['masterAdmin', 'admin'];
    const global = useCookieAuth
      ? sessionRoles.value?.global?.roles
      : authRoles.value?.global?.roles;
    const clients = useCookieAuth ? sessionRoles.value?.clients : authRoles.value?.clients;
    const inGlobal = SCHEDULE_ADMIN_ROLES.some((r) => (global ?? []).includes(r));
    const inClients = (clients ?? []).some((c) =>
      SCHEDULE_ADMIN_ROLES.some((r) => (c.roles ?? []).includes(r)),
    );
    return inGlobal || inClients;
  });

  const saveAuthToken = (token: string): void => {
    localStorage.setItem('authToken', token);
  };

  const getAuthToken = (): string | null => {
    return localStorage.getItem('authToken');
  };

  const saveAuthRoles = (roles: IAuthRoles): void => {
    localStorage.setItem('authRoles', JSON.stringify(roles));
  };

  const clearStoredAuth = (): void => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authRoles');
  };

  const getAuthRoles = (): IAuthRoles | null => {
    const stored = localStorage.getItem('authRoles');
    if (!stored) return null;

    try {
      return JSON.parse(stored);
    } catch {
      // A malformed value used to throw while the store was being built, which
      // stopped the app from mounting at all (PRX-2066). Drop it and carry on
      // unauthenticated so the router guard can send the user to login.
      localStorage.removeItem('authRoles');
      return null;
    }
  };

  const authToken: Ref<null | string> = ref(useCookieAuth ? null : getAuthToken());
  const employeeID: Ref<null | number> = ref(null);
  const currentEmployee: Ref<null | ICurrentEmployeeResponse> = ref(null);
  const authRoles: Ref<null | IAuthRoles> = ref(useCookieAuth ? null : getAuthRoles());
  const sessionRoles: Ref<null | RolesResponse> = ref(null);

  const fetchFeatureFlags = async (): Promise<void> => {
    try {
      // Fetch flags directly via service (works outside Vue component context)
      const flagsMap = await getFeatureFlagsService().getAllFlags();

      // Update the store's cache so composables can access the flags
      const featureFlagsStore = useFeatureFlagsStore();
      featureFlagsStore.flags = new Map(Object.entries(flagsMap));
      featureFlagsStore.lastFetched = new Date();
    } catch (err) {
      console.error('Failed to fetch feature flags:', err);
      // Don't block app load if feature flags fail
    }
  };

  const fetchSessionRoles = async (): Promise<void> => {
    try {
      sessionRoles.value = await getMyRoles();
    } catch (err) {
      console.error('Failed to fetch session roles:', err);
    }
  };

  /**
   * Drops every trace of the current session without calling the API. Used when
   * bootstrap fails, so the router guard treats the user as logged out.
   */
  const resetAuthState = (): void => {
    sessionActive.value = false;
    mfaRecommended.value = false;
    pendingMFAChallenge.value = null;
    pendingResetToken.value = null;
    pendingMFASetup.value = null;
    authToken.value = null;
    authRoles.value = null;
    employeeID.value = null;
    currentEmployee.value = null;
    sessionRoles.value = null;
    clearStoredAuth();
  };

  /**
   * Restores the session on app load.
   *
   * Never rejects. `main.ts` awaits this before `app.mount()`, so an error
   * escaping here left the page blank with no way back except clearing
   * localStorage by hand (PRX-2066). On failure we discard the session instead
   * and let the router guard redirect to login.
   */
  const bootstrap = async (): Promise<void> => {
    try {
      if (useCookieAuth) {
        clearStoredAuth();
        // 'offline' is treated like 'no-session': cookie mode saves nothing
        // to boot from, so the user signs in again once back online.
        const restored = (await restoreSession()) === 'ok';
        if (restored) {
          sessionActive.value = true;
          currentEmployee.value = await getCurrentEmployee();
          employeeID.value = currentEmployee.value?.ID || null;
          await fetchSessionRoles();
          await fetchFeatureFlags();
        }
        return;
      }

      if (authToken.value) {
        currentEmployee.value = await getCurrentEmployee();
        employeeID.value = currentEmployee.value?.ID || null;

        // Fetch feature flags on app load (after authentication)
        await fetchFeatureFlags();
      }
    } catch (err) {
      // Most often an expired token: /employees/me returns 401 and the axios
      // interceptor rejects.
      console.error('Auth bootstrap failed, continuing unauthenticated:', err);
      resetAuthState();
    }
  };

  const getCurrentEmployee = async (): Promise<ICurrentEmployeeResponse | null> => {
    const res = await legacyAxios.get('/api/v3/employees/me');
    return res.data;
  };

  const postLogin = async (username: string, password: string): Promise<IAuthResponse> => {
    if (useCookieAuth) {
      const result = await httpLogin(username, password);
      if (result.isSuccess()) {
        sessionActive.value = true;
        mfaRecommended.value = result.mfaRecommended;
        currentEmployee.value = await getCurrentEmployee();
        employeeID.value = currentEmployee.value?.ID || null;
        await fetchSessionRoles();
        await fetchFeatureFlags();
      } else if (result.requiresMFA()) {
        pendingMFAChallenge.value = result;
      } else if (result.requiresPasswordChange()) {
        pendingResetToken.value = result.challengeToken;
      } else if (result.requiresMFAEnrollment()) {
        pendingMFASetup.value = result;
      }
      // Return IAuthResponse-compatible stub so views work unchanged
      return {
        requiresSecondFactorAuth: result.requiresMFA(),
        requiresPasswordChange: result.requiresPasswordChange(),
        requiresMFASetup: result.requiresMFAEnrollment(),
      } as IAuthResponse;
    }

    const authReq: IAuthRequest = {
      username: username,
      password: password,
      version: packageInfo.version,
    };

    const res: AxiosResponse<IAuthResponse, IAuthRequest> = await legacyAxios.post(
      '/api/v1/authentication',
      authReq,
    );
    employeeID.value = res.data.employeeID;
    authToken.value = res.data.accessToken;
    authRoles.value = res.data.authRoles;
    saveAuthToken(res.data.accessToken);
    saveAuthRoles(res.data.authRoles);

    // Fetch feature flags after login
    await fetchFeatureFlags();

    return res.data;
  };

  /** Forgets the reset token, once it has been spent or has stopped working. */
  const clearResetToken = (): void => {
    pendingResetToken.value = null;
  };

  /** Forgets the MFA setup token, once it has been spent or has stopped working. */
  const clearMFASetup = (): void => {
    pendingMFASetup.value = null;
  };

  const logout = (): void => {
    pendingResetToken.value = null;
    pendingMFASetup.value = null;
    if (useCookieAuth) {
      httpLogout().catch(console.error);
      sessionActive.value = false;
      mfaRecommended.value = false;
      employeeID.value = null;
      currentEmployee.value = null;
      pendingMFAChallenge.value = null;
      sessionRoles.value = null;
      return;
    }

    authToken.value = null;
    employeeID.value = null;
    currentEmployee.value = null;
    authRoles.value = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('authRoles');
  };

  const postSecondFactor = async (code: string, trustDevice?: boolean): Promise<IAuthResponse> => {
    if (useCookieAuth) {
      if (!pendingMFAChallenge.value) {
        throw new Error('No pending MFA challenge');
      }
      const result = await httpVerifyMFA(pendingMFAChallenge.value, code, trustDevice);
      if (result.isSuccess()) {
        sessionActive.value = true;
        pendingMFAChallenge.value = null;
        currentEmployee.value = await getCurrentEmployee();
        employeeID.value = currentEmployee.value?.ID || null;
        await fetchSessionRoles();
        await fetchFeatureFlags();
      }
      // Return IAuthResponse-compatible stub so views work unchanged
      return {
        requiresSecondFactorAuth: false,
        requiresPasswordChange: result.requiresPasswordChange(),
      } as IAuthResponse;
    }

    const req: IAuthSecondFactorRequest = {
      employeeID: employeeID.value!,
      authCode: code,
    };
    const res: AxiosResponse<IAuthResponse, IAuthSecondFactorRequest> = await legacyAxios.post(
      '/api/v1/authentication/secondfactor',
      req,
    );
    // A wrong code comes back with no token (PRX-2351). Throw before touching the
    // saved token so the view shows the error instead of letting the user in.
    if (res.data.requiresSecondFactorAuth || !res.data.accessToken) {
      throw new Error('Incorrect second factor code');
    }
    authToken.value = res.data.accessToken;
    authRoles.value = res.data.authRoles;
    saveAuthToken(res.data.accessToken);
    saveAuthRoles(res.data.authRoles);

    // Fetch feature flags after second factor authentication
    await fetchFeatureFlags();

    return res.data;
  };

  const updatePendingChallenge = (challenge: HttpMFAChallenge): void => {
    pendingMFAChallenge.value = challenge;
  };

  const completeLogin = async (): Promise<void> => {
    sessionActive.value = true;
    pendingMFAChallenge.value = null;
    currentEmployee.value = await getCurrentEmployee();
    employeeID.value = currentEmployee.value?.ID || null;
    await fetchSessionRoles();
    await fetchFeatureFlags();
  };

  /**
   * Finishes the MFA step, given whatever /auth/mfa/verify returned — or what
   * MFA setup during login returned, which ends the same way.
   *
   * Passing MFA does not always mean the user is in: an expired password comes
   * back as `must_reset` with a challenge token and no session. Calling
   * `completeLogin` for that would mark the user signed in when the server has
   * given them nothing to be signed in with.
   *
   * An email-only user whose role requires TOTP or SMS comes back as
   * `mfa_enroll_required` after the email code: a setup token, still no
   * session (PRX-2355).
   *
   * Returns where the caller should send them.
   */
  const completeSecondFactor = async (result: AuthResult): Promise<'reset' | 'setup' | 'done'> => {
    if (result.requiresMFAEnrollment()) {
      pendingMFASetup.value = result;
      pendingMFAChallenge.value = null;
      return 'setup';
    }

    if (result.requiresPasswordChange()) {
      pendingResetToken.value = result.challengeToken;
      // The MFA step is over either way, so the challenge goes too. Leaving it
      // set would let a stale challenge outlive the flow that created it.
      pendingMFAChallenge.value = null;
      pendingMFASetup.value = null;
      return 'reset';
    }

    pendingMFASetup.value = null;
    await completeLogin();
    return 'done';
  };

  return {
    authToken,
    employeeID,
    currentEmployee,
    authRoles,
    sessionRoles,
    mfaRecommended,
    pendingMFAChallenge,
    pendingResetToken,
    clearResetToken,
    pendingMFASetup,
    clearMFASetup,
    bootstrap,
    logout,
    postLogin,
    postSecondFactor,
    updatePendingChallenge,
    completeLogin,
    completeSecondFactor,
    isAuthenticated,
    isSuperAdmin,
    isScheduleAdmin,
  };
});
